import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { boardAPI } from '../../api';
import { useAuthStore } from '../../store';
import './Board.css';

type TabType = 'notice' | 'free' | 'qna' | 'community';

interface Post {
  id: number;
  title: string;
  content: string;
  author_name?: string;
  views: number;
  created_at: string;
  is_important?: boolean;
  board_type?: string;
  category?: string;
}

const WriteModal: React.FC<{
  boardType: string;
  category: string;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ boardType, category, onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!title.trim()) { alert('제목을 입력해주세요.'); return; }
    if (!content.trim()) { alert('내용을 입력해주세요.'); return; }
    setSubmitting(true);
    try {
      await boardAPI.createPost({ title, content, board_type: boardType, category });
      alert('등록되었습니다.');
      onSuccess();
      onClose();
    } catch { alert('등록에 실패했습니다.'); }
    setSubmitting(false);
  };

  return (
    <div className="board-modal-overlay" onClick={onClose}>
      <div className="board-modal" onClick={e => e.stopPropagation()}>
        <div className="board-modal-header">
          <span>✏️ {category ? `[${category}] ` : ''}글쓰기</span>
          <button className="board-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="board-modal-body">
          <input className="form-control" placeholder="제목을 입력하세요"
            value={title} onChange={e => setTitle(e.target.value)}
            style={{ marginBottom: 12, fontSize: 14 }} />
          <textarea className="form-control" placeholder="내용을 입력하세요"
            value={content} onChange={e => setContent(e.target.value)}
            rows={10} style={{ resize: 'vertical', fontSize: 14 }} />
        </div>
        <div className="board-modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>취소</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? '등록 중...' : '등록'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DetailModal: React.FC<{ post: Post; onClose: () => void }> = ({ post, onClose }) => (
  <div className="board-modal-overlay" onClick={onClose}>
    <div className="board-modal" onClick={e => e.stopPropagation()}>
      <div className="board-modal-header">
        <span>{post.title}</span>
        <button className="board-modal-close" onClick={onClose}>✕</button>
      </div>
      <div className="board-modal-body">
        <div className="board-detail-meta">
          <span>👤 {post.author_name || '관리자'}</span>
          <span>📅 {post.created_at?.slice(0, 10)}</span>
          <span>👁️ {post.views}</span>
          {post.category && <span>📂 {post.category}</span>}
        </div>
        <div className="board-detail-content">{post.content}</div>
      </div>
      <div className="board-modal-footer">
        <button className="btn btn-ghost" onClick={onClose}>닫기</button>
      </div>
    </div>
  </div>
);

const BoardPage: React.FC = () => {
  const { isLoggedIn } = useAuthStore();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category') || '';

  const [tab, setTab] = useState<TabType>(categoryParam ? 'community' : 'notice');
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [showWrite, setShowWrite] = useState(false);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (tab === 'notice') {
        const res = await boardAPI.notices();
        setPosts(res.data);
      } else {
        const res = await boardAPI.posts(tab, tab === 'community' ? categoryParam : undefined);
        setPosts(res.data);
      }
    } catch { setPosts([]); }
    setLoading(false);
    setPage(1);
  }, [tab, categoryParam]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (categoryParam) setTab('community'); }, [categoryParam]);

  const filtered = posts.filter(p =>
    !searchKeyword.trim() ||
    p.title.includes(searchKeyword.trim()) ||
    p.content?.includes(searchKeyword.trim())
  );
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const getTabTitle = () => {
    if (tab === 'community' && categoryParam) return `💬 ${categoryParam} 게시판`;
    if (tab === 'notice') return '📢 공지사항';
    if (tab === 'free') return '📋 자유게시판';
    if (tab === 'qna') return '❓ Q&A';
    return '💬 커뮤니티';
  };

  return (
    <Layout>
      <div className="board-page">
        <div className="board-header">
          <div className="board-header-left">
            <h2 className="board-title">{getTabTitle()}</h2>
            {!categoryParam ? (
              <div className="board-tabs">
                {([
                  { key: 'notice',    label: '📢 공지사항' },
                  { key: 'free',      label: '📋 자유게시판' },
                  { key: 'qna',       label: '❓ Q&A' },
                  { key: 'community', label: '💬 커뮤니티' },
                ] as { key: TabType; label: string }[]).map(t => (
                  <button key={t.key}
                    className={`board-tab ${tab === t.key ? 'active' : ''}`}
                    onClick={() => { setTab(t.key); setSearchKeyword(''); }}>
                    {t.label}
                  </button>
                ))}
              </div>
            ) : (
              <div className="board-category-badge">📂 {categoryParam} 전용 게시판</div>
            )}
          </div>
          <div className="board-header-right">
            <div className="board-search">
              <input className="board-search-input" placeholder="🔍 검색..."
                value={searchKeyword}
                onChange={e => { setSearchKeyword(e.target.value); setPage(1); }} />
              {searchKeyword && (
                <button className="board-search-clear" onClick={() => setSearchKeyword('')}>✕</button>
              )}
            </div>
            {tab !== 'notice' && isLoggedIn && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowWrite(true)}>✏️ 글쓰기</button>
            )}
          </div>
        </div>

        <div className="board-content">
          {loading ? (
            <div className="loading-spinner"><div className="spinner" /></div>
          ) : paginated.length === 0 ? (
            <div className="empty-state" style={{ padding: 60 }}>
              <div className="icon">{searchKeyword ? '🔍' : '📭'}</div>
              <p>{searchKeyword ? `"${searchKeyword}" 검색 결과가 없습니다.` : '등록된 글이 없습니다. 첫 글을 작성해보세요!'}</p>
            </div>
          ) : (
            <table className="board-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>번호</th>
                  <th>제목</th>
                  <th style={{ width: 100 }}>작성자</th>
                  <th style={{ width: 100 }}>작성일</th>
                  <th style={{ width: 60 }}>조회</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((post, i) => (
                  <tr key={post.id}
                    className={`board-row ${post.is_important ? 'important' : ''}`}
                    onClick={() => setSelectedPost(post)}>
                    <td style={{ textAlign: 'center' }}>
                      {post.is_important ? <span className="badge badge-danger">중요</span> : (page - 1) * PAGE_SIZE + i + 1}
                    </td>
                    <td className="board-row-title">
                      {post.is_important && <span className="board-important-mark">📌 </span>}
                      {post.title}
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{post.author_name || '관리자'}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{post.created_at?.slice(0, 10)}</td>
                    <td style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>{post.views}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* 페이지네이션 - 항상 표시 */}
        <div className="board-pagination">
          <span className="board-page-info">
            총 {filtered.length}건 / {totalPages || 1}페이지
          </span>
          <button className="board-page-btn" onClick={() => setPage(1)} disabled={page === 1}>«</button>
          <button className="board-page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
          {Array.from({ length: Math.max(totalPages, 1) }, (_, i) => i + 1)
            .filter(p => p >= page - 2 && p <= page + 2)
            .map(p => (
              <button key={p} className={`board-page-btn ${p === page ? 'active' : ''}`}
                onClick={() => setPage(p)}>{p}</button>
            ))}
          <button className="board-page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>›</button>
          <button className="board-page-btn" onClick={() => setPage(totalPages || 1)} disabled={page >= totalPages}>»</button>
        </div>

        {showWrite && tab !== 'notice' && (
          <WriteModal
            boardType={tab === 'community' ? 'community' : tab}
            category={categoryParam}
            onClose={() => setShowWrite(false)}
            onSuccess={load}
          />
        )}

        {selectedPost && (
          <DetailModal post={selectedPost} onClose={() => setSelectedPost(null)} />
        )}
      </div>
    </Layout>
  );
};

export default BoardPage;
